import {Modal} from '#/atoms/modal';
import {NoticeMessageErrorDetails} from '#/atoms/notice-message-error-details';
import {
  WalletTransferContext,
  WalletTransferContextProvider,
} from '#/components/wallet-transfer/wallet-transfer-context';
import {WalletTransferTab} from '#/components/wallet-transfer/wallet-transfer-tab';
import {WalletTransferTabsContent} from '#/components/wallet-transfer/wallet-transfer-tabs-content';
import {WalletTransferTabsList} from '#/components/wallet-transfer/wallet-transfer-tabs-list';
import {WalletTransferTabsRoot} from '#/components/wallet-transfer/wallet-transfer-tabs-root';
import {ModalContext} from '#/contexts/modal-provider';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {ArrowPathIcon, CheckIcon} from '@heroicons/react/24/solid';
import {ComponentProps, FunctionComponent, createContext, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const WalletTransferModal: FunctionComponent = () => {
  const {close} = useContext(ModalContext);
  const {t} = useTranslation('translation');

  return (
    <Modal title={t('pages.walletTransfer.title')} onClose={close}>
      <WalletTransferContextProvider>
        <WalletTransferTabsRoot
          className='grow p-4 font-semibold text-sm grid grid-rows-[auto,auto] grid-cols-1 sm:grid-rows-1 sm:grid-cols-[auto,340px] gap-4'
          {...({} as any)}
        >
          <WalletTransferTabsList className='flex flex-col justify-stretch gap-y-2' />
          <WalletTransferTabsContent className='sm:border-l sm:border-gray-400 sm:pl-4' />
        </WalletTransferTabsRoot>
      </WalletTransferContextProvider>
    </Modal>
  );
};

export const ActionNextStepButtonContainer: FunctionComponent<DefaultChildren> = ({children}) => (
  <div className='flex gap-2'>{children}</div>
);

export const SkipNextButton: FunctionComponent<{active: boolean; onClick?: () => void}> = ({active, onClick}) => {
  const {tab, setTab} = useContext(WalletTransferContext);

  return (
    <TokenTransferTitleButton
      className='justify-self-end w-full bg-transparent border border-gray-400 text-gray-400 data-[active=true]:bg-deepblue data-[active=true]:border-transparent data-[active=true]:text-white'
      onClick={() => {
        setTab(Object.values(WalletTransferTab)[Object.values(WalletTransferTab).indexOf(tab) + 1]);
        onClick?.();
      }}
      data-active={active}
    >
      {active ? 'Next' : 'Skip'}
    </TokenTransferTitleButton>
  );
};

export const Title: FunctionComponent<DefaultChildren> = ({children}) => (
  <div className='mb-2 text-gray-400 text-sm'>{children}</div>
);

type TokenTransferStatusContextValue = {
  status: 'idle' | 'loading' | 'success' | 'error';
  enabled: boolean;
  error: Error | null;
  start: () => void;
  bgColor: `bg-${string}`;
};
export const TokenTransferStatusContext = createContext<TokenTransferStatusContextValue>(
  {} as TokenTransferStatusContextValue,
);

export const TokenTransferStatusProvider: FunctionComponent<
  {queryEnabled: boolean; queryStatus?: string; queryError: Error | null; queryStart: () => void} & DefaultChildren
> = ({children, queryEnabled, queryStatus, queryError, queryStart}) => {
  const {status, bgColor} = (() => {
    if (queryStatus === 'idle') {
      return {
        status: 'idle',
        bgColor: 'bg-gray-400',
      };
    }

    if (!!queryError) {
      return {
        status: 'error',
        bgColor: 'bg-downred',
      };
    }

    if (queryStatus === 'loading') {
      return {
        status: 'loading',
        bgColor: 'bg-yellow-500',
      };
    }

    return {
      status: 'success',
      bgColor: 'bg-upgreen',
    };
  })();
  return (
    <TokenTransferStatusContext.Provider
      value={
        {
          status,
          bgColor,
          enabled: queryEnabled,
          error: queryError,
          start: queryStart,
        } as TokenTransferStatusContextValue
      }
    >
      {children}
    </TokenTransferStatusContext.Provider>
  );
};

export const StepHeader: FunctionComponent<DefaultChildren> = ({children}) => (
  <h2 className='text-base text-gray-500 font-bold border-b border-gray-200 pb-2 mb-2'>{children}</h2>
);

export const TokenTransferStatusTitle: FunctionComponent<DefaultChildren> = ({children}) => {
  const {bgColor} = useContext(TokenTransferStatusContext);

  return (
    <div className='flex items-center gap-2 whitespace-nowrap overflow-hidden flex-1'>
      <div className={`w-3 h-3 rounded-full ${bgColor} justify-self-start`} />
      <span className='font-bold capitalize justify-self-start whitespace-nowrap'>{children}</span>
    </div>
  );
};

export const TokenTransferTitleButton: FunctionComponent<ComponentProps<'button'>> = ({
  className,
  children,
  ...rest
}) => (
  <button
    className={`h-8 min-w-fit align-middle py-1 px-2 overflow-hidden rounded hover:opacity-70 disabled:hover:opacity-100 disabled:cursor-not-allowed ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const TokenTransferTitleIcon: FunctionComponent<ClassName & DefaultChildren> = ({className, children}) => (
  <div className={`h-8 min-w-fit flex items-center gap-1 ${className}`}>{children}</div>
);

export const TokenTransferSingleStepButton: FunctionComponent = () => {
  const {status, enabled, start} = useContext(TokenTransferStatusContext);
  const {t} = useTranslation('translation');

  return (
    <>
      {status === 'idle' && (
        <TokenTransferTitleButton
          className='w-full text-white bg-deepblue disabled:bg-gray-400/40'
          onClick={start}
          disabled={!enabled}
        >
          {t('actions.transfer')}
        </TokenTransferTitleButton>
      )}
      {status === 'loading' && (
        <TokenTransferTitleButton className='w-full text-white bg-yellowNotice animate-pulse' disabled={true}>
          {t('statuses.loading')}
        </TokenTransferTitleButton>
      )}
      {status === 'error' && (
        <TokenTransferTitleButton className='w-full text-white bg-downred' onClick={start}>
          {t('actions.retry')}
        </TokenTransferTitleButton>
      )}
      {status === 'success' && (
        <TokenTransferTitleButton className='w-full flex-center gap-2 bg-upgreen/70 text-white' disabled={true}>
          <CheckIcon className={'w-4 h-4 fill-white stroke-white'} />
          {t('statuses.success')}
        </TokenTransferTitleButton>
      )}
    </>
  );
};

export const TokenTransferCompoundStepButton: FunctionComponent = () => {
  const {status, start} = useContext(TokenTransferStatusContext);
  const {t} = useTranslation('translation');

  return (
    <>
      {status === 'loading' && (
        <TokenTransferTitleIcon className='text-gray-400'>
          <ArrowPathIcon className={'w-4 h-4 fill-gray-400 stroke-gray-400 animate-spin'} />
        </TokenTransferTitleIcon>
      )}
      {status === 'error' && (
        <TokenTransferTitleButton className='w-14 text-white bg-downred' onClick={start}>
          {t('actions.retry')}
        </TokenTransferTitleButton>
      )}
      {status === 'success' && (
        <TokenTransferTitleIcon className='text-upgreen'>
          <CheckIcon className={'w-4 h-4 fill-upgreen stroke-upgreen'} />
        </TokenTransferTitleIcon>
      )}
    </>
  );
};

export const TokenTransferStatusContainer: FunctionComponent<DefaultChildren> = ({children}) => {
  const {error} = useContext(TokenTransferStatusContext);

  return (
    <div className='self-center space-y-2 text-sm'>
      <div className='flex justify-between items-center gap-4 h-8'>{children}</div>
      {!!error && <NoticeMessageErrorDetails error={error as Error} />}
    </div>
  );
};
